import { sanitizeInput } from './inputValidation'

/**
 * HTML Sanitization utilities to prevent XSS attacks
 */

// Allowed HTML tags for rich text content
const ALLOWED_TAGS = [
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'span', 'div',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li', 'blockquote',
  'a', 'img'
]

// Allowed attributes for specific tags
const ALLOWED_ATTRIBUTES: { [key: string]: string[] } = {
  'a': ['href', 'title', 'target'],
  'img': ['src', 'alt', 'width', 'height', 'class'],
  'div': ['class'],
  'span': ['class'],
  'p': ['class'],
  'h1': ['class'], 'h2': ['class'], 'h3': ['class'], 'h4': ['class'], 'h5': ['class'], 'h6': ['class']
}

/**
 * Sanitize HTML content by removing dangerous elements and attributes
 */
export const sanitizeHTML = (html: string): string => {
  if (!html || typeof html !== 'string') {
    return ''
  }

  // Basic input sanitization first
  let sanitized = sanitizeInput(html)

  // Remove dangerous protocols
  sanitized = sanitized.replace(/javascript:/gi, '')
  sanitized = sanitized.replace(/vbscript:/gi, '')
  sanitized = sanitized.replace(/data:(?!image\/)/gi, '') // Allow only data:image/

  // Remove event handlers
  sanitized = sanitized.replace(/on\w+\s*=/gi, '')

  // Remove script tags and their content
  sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')

  // Remove style tags (but allow class attributes)
  sanitized = sanitized.replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')

  // Remove dangerous tags
  const dangerousTags = ['iframe', 'object', 'embed', 'form', 'input', 'button', 'textarea', 'select', 'option']
  dangerousTags.forEach(tag => {
    const regex = new RegExp(`<${tag}\\b[^>]*>.*?<\\/${tag}>`, 'gi')
    sanitized = sanitized.replace(regex, '')
    // Also remove self-closing versions
    const selfClosingRegex = new RegExp(`<${tag}\\b[^>]*\\/?>`, 'gi')
    sanitized = sanitized.replace(selfClosingRegex, '')
  })

  return sanitized
}

/**
 * Sanitize HTML for visual builder elements (more restrictive)
 */
export const sanitizeVisualBuilderHTML = (html: string): string => {
  if (!html || typeof html !== 'string') {
    return ''
  }

  // Only allow very basic formatting
  let sanitized = html

  // Remove all script-related content
  sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
  sanitized = sanitized.replace(/javascript:/gi, '')
  sanitized = sanitized.replace(/on\w+\s*=/gi, '')

  // Only allow basic text formatting
  const allowedBasicTags = ['strong', 'b', 'em', 'i', 'u', 'br', 'p']
  
  // Remove all HTML tags except the allowed ones
  sanitized = sanitized.replace(/<\/?(?!(?:strong|b|em|i|u|br|p)\b)[^>]*>/gi, '')

  return sanitized
}

/**
 * Escape HTML entities to prevent XSS
 */
export const escapeHTML = (text: string): string => {
  if (!text || typeof text !== 'string') {
    return ''
  }

  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

/**
 * Safe way to render user content - escapes by default, only allows HTML if explicitly sanitized
 */
export const renderSafeContent = (content: string, allowHTML: boolean = false): string => {
  if (!content || typeof content !== 'string') {
    return ''
  }

  if (allowHTML) {
    return sanitizeHTML(content)
  }

  return escapeHTML(content)
}

/**
 * Validate and sanitize URLs to prevent malicious redirects
 */
export const sanitizeURL = (url: string): string => {
  if (!url || typeof url !== 'string') {
    return ''
  }

  // Remove dangerous protocols
  if (url.match(/^(javascript|vbscript|data|file):/i)) {
    return ''
  }

  // Ensure relative URLs start with / or # or are full https/http URLs
  if (!url.match(/^(https?:\/\/|\/|#)/i)) {
    return ''
  }

  return url
}